import React, { useEffect, useRef, useState } from 'react';
import { Box, Drawer, IconButton, Stack } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation } from 'react-router-dom';
import Content from 'dashboard/components/sidebar/components/Content';

/**
 * Sidebar shell. Uses native `overflow-y: auto` instead of
 * react-custom-scrollbars-2 — the old wrapper's view renderer had a
 * `marginBottom: -22` hack that clipped the last menu item.
 */
function Sidebar({ routes, mini, hovered, setHovered, selectedBrand, brandList, setSelectedBrand }) {
  const width = mini === false || (mini === true && hovered === true) ? '285px' : '120px';

  return (
    <Box
      sx={{
        display: { xs: 'none', xl: 'block' },
        position: 'fixed',
        height: '100vh',
        zIndex: 100,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          transition: 'width 0.2s linear',
          width,
          height: 'calc(100vh - 32px)',
          mt: '16px',
          ml: '16px',
          borderRadius: '30px',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxShadow: (t) => (t.palette.mode === 'dark'
            ? '14px 17px 40px 4px rgba(0, 0, 0, 0.40)'
            : '14px 17px 40px 4px rgba(112, 144, 176, 0.08)'),
          // Hide the scrollbar visually until hovered.
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': { scrollbarColor: 'rgba(112, 144, 176, 0.3) transparent' },
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: 3,
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(112, 144, 176, 0.3)',
          },
        }}
      >
        <Content
          mini={mini}
          hovered={hovered}
          routes={routes}
          selectedBrand={selectedBrand}
          brandList={brandList}
          setSelectedBrand={setSelectedBrand}
        />
      </Box>
    </Box>
  );
}

export function SidebarResponsive({ routes }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef();
  const { pathname } = useLocation();

  // Close on navigation.
  //
  // The drawer covers the page it just navigated to, so picking a menu item
  // left you looking at the menu again with the thing you asked for hidden
  // behind it — on a phone that is the whole screen. Watching the pathname
  // rather than passing an onClick down through Content and Links means every
  // item closes it, including the nested accordion children, and any link
  // added later does too without anyone remembering to wire it up.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <Stack
      direction='row'
      sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'center' }}
    >
      <IconButton ref={btnRef} onClick={() => setOpen(true)} sx={{ mr: '10px' }}>
        <MenuIcon />
      </IconButton>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
        PaperProps={{
          sx: {
            width: '285px',
            maxWidth: '285px',
            borderRadius: '16px',
            m: '16px',
            overflowY: 'auto',
          },
        }}
      >
        {/* Tapping the item you are already on does not change the pathname,
            so the effect above would not fire and the drawer would sit there
            looking broken. Closing on any click that lands inside a link
            covers that without threading a callback through Content and
            Links; clicks on a group header, which only expands it, are
            ignored because they are not links. */}
        <Box
          sx={{ height: '100%' }}
          onClick={(e) => { if (e.target.closest('a')) setOpen(false); }}
        >
          <Content mini={false} routes={routes} />
        </Box>
      </Drawer>
    </Stack>
  );
}

export default Sidebar;
